import {
  Button,
  IconCancel,
  IconCheckCircle,
  useLayout,
  useMediaQuery,
} from '@capitalise/ui-design-system';
import { ModalProvider } from 'hooks/useModalContext';
import { SignupType } from 'modules/AuthAlpha/shared/types';
import Toast from 'modules/Business/components/Toast';
import OnboardingWizzardAlpha from 'modules/WizzardAlpha/WizzardAlpha';
import { useMutationPostInviteUser } from 'services/auth/queries';
import { getOnboardingFlow } from '../flows';
import { Map } from 'immutable';
import { UserType } from 'enums/user-type';
import { MouseEventHandler, useEffect } from 'react';
import useQuestionaireContext from 'modules/Business/hooks/useQuestionaireContext';
import useDisplayDirectorConsent from 'modules/Business/hooks/useDisplayDirectorConsent';
import { MembershipLevels } from 'modules/Business/shared/typesTS';
import { useRedirectTrackingPaymentOrigin } from '@capitalise/common';

interface IOnboardingAlphaProps {
  skipOnboarding: MouseEventHandler<HTMLButtonElement>;
  addNewTeam: Function;
  onCompleteOnboarding: Function;
  creditReportDownloads: number | boolean;
  firstActiveSearch: any;
  goToRoute: Function;
  isAuthenticated: boolean;
  onboardingType: string;
  playbook: boolean;
  requestMyTeam: Function;
  userCompanyId: number;
  userConsultant: object;
  userType: string;
  capitalReport: boolean;
  hasNewDesignSystem: boolean;
  team: any;
  apiKey: string;
  user: any;
}

const OnboardingAlpha = ({
  skipOnboarding,
  team,
  addNewTeam,
  onCompleteOnboarding,
  creditReportDownloads,
  firstActiveSearch,
  goToRoute,
  playbook,
  userCompanyId,
  userConsultant,
  userType,
  capitalReport,
  hasNewDesignSystem,
  apiKey,
  onboardingType,
  user,
}: IOnboardingAlphaProps) => {
  const { renderToaster } = useLayout();
  const { redirectToPaymentFlow } = useRedirectTrackingPaymentOrigin();
  const { setIsOpen: setOpenQuestionnaire } = useQuestionaireContext();
  const { showDirectorConsent: shouldDisplayDirectorConsent, isLoading } =
    useDisplayDirectorConsent();
  const { isMobile } = useMediaQuery();

  const { mutateAsync } = useMutationPostInviteUser();
  const teamId = team.toJS()?.id;

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  const sendInviteSignup = (metaData: any) => {
    const emailList = metaData.inviteEmails || [];

    if (emailList.length === 0) {
      return;
    }

    const onSuccess = () => {
      renderToaster(<Toast icon={IconCheckCircle} text="Colleagues invited successfully" />);
      return;
    };

    const onError = () => {
      renderToaster(
        <Toast icon={IconCancel} text="Failed to invite colleagues, please try again later" />,
      );
      throw 'Failed to invite colleagues';
    };

    return mutateAsync(
      {
        type: SignupType.introducerTeam,
        emails: emailList,
        team: teamId,
      } as unknown as void,
      { onSuccess, onError },
    );
  };

  const flowConfig = getOnboardingFlow(onboardingType, {
    addNewTeam,
    companyId: userCompanyId,
    completeOnboarding: onCompleteOnboarding,
    consultant: userConsultant || Map(),
    creditReports: creditReportDownloads || 50,
    searchId: firstActiveSearch ? firstActiveSearch.get('id') : null,
    entityId: capitalReport ? userCompanyId : null,
    goToRoute,
    redirectToPaymentFlow,
    isIntroducer: userType === UserType.introducer,
    playbook,
    hasNewDesignSystem,
    sendInviteSignup,
    renderToaster,
    setOpenQuestionnaire,
    apiKey,
    shouldDisplayDirectorConsent,
    shouldDisplayPlans: user.toJS()?.membership?.level?.name === MembershipLevels.free,
    user,
  });

  if (!flowConfig || flowConfig.length === 0 || isLoading) {
    return null;
  }

  const headerActions = () => {
    return (
      <Button
        data-testid="skip-onboarding-button"
        variant="outlined"
        baseColor="green"
        onClick={skipOnboarding}
      >
        Skip onboarding
      </Button>
    );
  };

  const defaultContext = {
    label: 'OnboardingModal',
    defaultValue: true,
    content: OnboardingWizzardAlpha,
    defaultMetaData: {
      stepsConfig: flowConfig,
      skipOnboarding: skipOnboarding,
      headerActions,
      stepper: { variant: isMobile ? 'condensed' : 'default' },
    },
  };

  return <ModalProvider defaultContext={defaultContext} />;
};

export default OnboardingAlpha;
